import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { FaBan, FaCheck, FaClock } from 'react-icons/fa'
import type { PermState } from './toolPermissions'

const OPTIONS: { value: PermState; icon: React.ReactNode }[] = [
  { value: 'allow', icon: <FaCheck /> },
  { value: 'approval', icon: <FaClock /> },
  { value: 'disabled', icon: <FaBan /> }
]

interface Props {
  // null renders as "mixed" (used for a group whose tools don't share one state).
  value: PermState | null
  onChange: (state: PermState) => void
}

// Segmented 3-state control: allow / needs-approval / disabled.
export const ToolPermissionControl: React.FC<Props> = ({ value, onChange }) => {
  const [t] = useTranslation()
  return (
    <Segmented role="group">
      {OPTIONS.map(option => (
        <Segment
          key={option.value}
          type="button"
          $active={value === option.value}
          $state={option.value}
          aria-pressed={value === option.value}
          title={t(`lucaSettings.approval.state.${option.value}`)}
          aria-label={t(`lucaSettings.approval.state.${option.value}`)}
          onClick={() => onChange(option.value)}
        >
          {option.icon}
        </Segment>
      ))}
    </Segmented>
  )
}

const ACTIVE_COLOR: Record<PermState, 'success' | 'warning' | 'error'> = {
  allow: 'success',
  approval: 'warning',
  disabled: 'error'
}

const Segmented = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  flex: 0 0 auto;
`

const Segment = styled.button<{ $active: boolean; $state: PermState }>`
  width: 1.9rem;
  height: 1.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  cursor: pointer;
  background: ${({ $active, theme }) => ($active ? theme.colors.neutralWhite : 'transparent')};
  color: ${({ $active, $state, theme }) =>
    $active ? theme.colors[ACTIVE_COLOR[$state]] : theme.colors.iconGray};
  box-shadow: ${({ $active }) => ($active ? '0 1px 2px rgba(0, 0, 0, 0.12)' : 'none')};
  transition: background 0.12s ease, color 0.12s ease;

  svg {
    width: 0.8rem;
    height: 0.8rem;
  }

  &:hover {
    color: ${({ $active, $state, theme }) =>
      $active ? theme.colors[ACTIVE_COLOR[$state]] : theme.colors.iconGrayDark};
  }
`
